import { CardBrand, Country, Feature } from '@afterpay/types'
import { PCL_CARD_EXPIRY_LIMIT_IN_MONTHS } from '../../utils/credit-card'
import { InputAddressData } from '@afterpaytouch/core'
import { AddressFieldSchema, CardFieldSchema, CheckboxFieldSchema, DropdownFieldSchema, FieldType, Schema } from '@afterpaytouch/form'
import { ConsumerAddress } from '@afterpaytouch/portal-api/consumer/types'
import { TFunction } from 'i18next'
import { app } from '../../env'
import { CardErrorReason, NewCard, PaymentMethodType, SelectedPaymentMethod } from '../../model'
import { ConsumerProfile } from '../../state'
import { getAddressOptionalFields } from '../../utils/profile-validation'
import { requiredAddress } from '../ShippingAddressForm/validations'
import { COUNTRIES_WITH_BILLING_ADDRESS_REQUIREMENTS } from './constants'
import { FormCardDetails, FormComponents, NewPaymentMethodFormSchema, SchemaUpdates } from './types'
import { defaultError } from './validations'

interface GetSchemaAndFormDataResult {
  schema: Schema<NewPaymentMethodFormSchema>
  initialFormData: NewPaymentMethodFormSchema
}

type SupportedCardTypeFeature = Feature.PBI | Feature.PCL | Feature.AFF
type TranslationSelector = (t: TFunction) => string
type BinNotAcceptedReason = CardErrorReason | 'unknown'

const CARD_TYPE_TRANSLATORS: Record<SupportedCardTypeFeature, TranslationSelector> = {
  [Feature.PBI]: (t) => t('paymentMethod:cardType.PBI'),
  [Feature.PCL]: (t) => t('paymentMethod:cardType.PCL'),
  [Feature.AFF]: (t) => t('paymentMethod:cardType.AFF'),
}
const DEFAULT_CARD_TYPE_TRANSLATOR = CARD_TYPE_TRANSLATORS[Feature.PBI]

const BIN_NOT_ACCEPTED_TRANSLATORS: Record<BinNotAcceptedReason, TranslationSelector> = {
  invalidBrand: (t) => t('paymentMethod:errors.invalidBrand'),
  cardExpiry: (t) => t('paymentMethod:errors.cardExpiry'),
  achNotAllowed: (t) => t('paymentMethod:errors.achNotAllowed'),
  creditCardNotAllowed: (t) => t('paymentMethod:errors.creditCardNotAllowed'),
  cardExpiryLimitInMonths: (t) => t('paymentMethod:errors.cardExpiryLimitInMonths', { count: PCL_CARD_EXPIRY_LIMIT_IN_MONTHS }),
  cashAppCardIneligible: (t) => t('paymentMethod:errors.cashAppCardIneligible'),
  dssProhibitedPaymentMethod: (t) => t('paymentMethod:errors.dssProhibitedPaymentMethod'),
  fallbackGenericMessage: (t) => t('paymentMethod:errors.fallbackGenericMessage'),
  unacceptableIssuerRestrictedCard: (t) => t('paymentMethod:errors.unacceptableIssuerRestrictedCard'),
  unacceptableCard: (t) => t('paymentMethod:errors.unacceptableCard'),
  unknown: (t) => t('paymentMethod:errors.unknown'),
}

const getCardTypeTitle = (feature: Feature, t: TFunction): string => {
  const translateCardType = CARD_TYPE_TRANSLATORS[feature as SupportedCardTypeFeature] ?? DEFAULT_CARD_TYPE_TRANSLATOR
  return translateCardType(t)
}

const isBinNotAcceptedReason = (reason: string): reason is BinNotAcceptedReason => Object.prototype.hasOwnProperty.call(BIN_NOT_ACCEPTED_TRANSLATORS, reason)

const selectBinNotAcceptedReason = (reason: string | undefined): BinNotAcceptedReason => {
  if (reason && isBinNotAcceptedReason(reason)) {
    return reason
  }

  return 'unknown'
}

const getBinNotAcceptedErrorMessage = (cardNumber: string, blockedBins: string[], binsToErrorsMap: Record<string, CardErrorReason>, t: TFunction): string => {
  const blockedBin = blockedBins.find((bin) => cardNumber.startsWith(bin))
  const reasonFromMap = blockedBin ? binsToErrorsMap[blockedBin] : undefined
  const errorReason = selectBinNotAcceptedReason(reasonFromMap)
  return BIN_NOT_ACCEPTED_TRANSLATORS[errorReason](t)
}

export const getDropdownFieldSchema = ({ label, value = '', items, options, id, error = false }): DropdownFieldSchema => ({
  type: FieldType.Dropdown,
  props: {
    label,
    defaultValue: value,
    id,
    error,
  },
  items,
  options,
})

const getAddressFieldSchema = ({ value, error = false, otherProps = {}, options = {}, disabled, show = false, testNameSpace }): AddressFieldSchema => ({
  type: FieldType.Address,
  apiKey: app.GOOGLEMAPS_API_KEY,
  show,
  props: {
    initialAddress: value,
    error,
    disabled,
    ...otherProps,
  },
  options,
  testNameSpace,
})

const getCardFieldSchema = ({
  acceptableCardBrands,
  defaultValues,
  errorMessages,
  fieldTitle,
  fieldTitleSize,
  labels,
  lastPaymentDate,
  blockedBins,
  showCardHolderNameField,
  showHeading,
  testNameSpace,
  feature,
  showCardIcon,
  customizedHeading,
}: Omit<CardFieldSchema, 'type'>): CardFieldSchema => ({
  acceptableCardBrands,
  defaultValues,
  lastPaymentDate,
  errorMessages,
  fieldTitle,
  fieldTitleSize,
  labels,
  showCardHolderNameField,
  type: FieldType.Card,
  showHeading,
  customizedHeading,
  blockedBins,
  testNameSpace,
  feature,
  showCardIcon,
})

const getCheckboxFieldSchema = ({ value, error = false, otherProps = {}, options = {}, show = true }): CheckboxFieldSchema => ({
  type: FieldType.Checkbox,
  show,
  props: {
    defaultChecked: value,
    ...otherProps,
  },
  options,
})

export const getCardDetailsFromState = (
  selected: SelectedPaymentMethod | undefined,
  profile: ConsumerProfile,
  hasSavedPaymentMethods: boolean
): FormCardDetails => {
  const hasCreditCardBeenSelected = selected?.type === PaymentMethodType.CREDIT_CARD
  if (hasCreditCardBeenSelected && !hasSavedPaymentMethods && 'card' in selected) {
    const { card } = selected
    const { cardHolderName, cardNumber, cardExpiryMonth, cardExpiryYear } = card as NewCard
    const hasExpiryMonth = cardExpiryMonth !== undefined
    const hasExpiryYear = cardExpiryYear !== undefined
    return {
      cardHolderName: cardHolderName ?? '',
      cardNumber: cardNumber ?? '',
      cvv: '',
      expiryDate: hasExpiryMonth && hasExpiryYear ? `${cardExpiryMonth}/${cardExpiryYear}` : '',
    }
  }
  return {
    cardHolderName: `${profile?.givenNames ?? ''} ${profile?.surname ?? ''}`,
    cardNumber: '',
    cvv: '',
    expiryDate: '',
  }
}

export const getInitialAddressDetails = (profileAddress?: ConsumerAddress): InputAddressData => {
  return {
    address1: profileAddress?.address1 ?? '',
    address2: profileAddress?.address2 ?? '',
    state: profileAddress?.state ?? '',
    postcode: profileAddress?.postcode ?? '',
    suburb: profileAddress?.suburb ?? '',
    area2: '',
    countryCode: profileAddress?.countryCode ?? '',
  }
}

export const getAddressPreview = ({ address1, address2, area2, suburb, state, postcode }: InputAddressData): string =>
  `${address1} ${address2} ${area2} ${suburb}, ${state} ${postcode}`

export const getTransformedAddress = (addressDetails?: InputAddressData | ConsumerAddress, countryCode?: Country): NewCard['address'] | undefined => {
  const address = {
    city: addressDetails?.suburb,
    country: countryCode,
    postcode: addressDetails?.postcode,
    state: addressDetails?.state,
    street1: addressDetails?.address1,
    street2: addressDetails?.address2 ?? undefined,
  }

  return COUNTRIES_WITH_BILLING_ADDRESS_REQUIREMENTS.includes(countryCode) ? address : undefined
}

export const getNewCardDetails = (formData: NewPaymentMethodFormSchema, profileAddress?: ConsumerAddress): NewCard => {
  const {
    cardField: { cardHolderName, cvv, cardNumber, expiryDate },
    address = profileAddress,
  } = formData
  const transformedAddress = getTransformedAddress(address, profileAddress?.countryCode)
  const [month, year] = expiryDate.split('/')

  return {
    cardHolderName,
    cardNumber,
    cardExpiryMonth: month,
    cardExpiryYear: year,
    address: transformedAddress,
    cardSecurityCode: cvv,
  }
}

const getInitialFormData = (initialCardDetails: FormCardDetails, initialAddressDetails: InputAddressData): NewPaymentMethodFormSchema => {
  return {
    cardField: initialCardDetails,
    address: initialAddressDetails,
  }
}

export const getSchemaAndFormData = (
  components: FormComponents,
  t: TFunction,
  initialCardDetails: FormCardDetails,
  initialAddressDetails: InputAddressData,
  accountCountry: Country,
  enabledCardTypes: CardBrand[],
  lastPaymentDate: Date | null,
  schemaUpdates: SchemaUpdates,
  feature: Feature,
  binsToErrorsMap: Record<string, CardErrorReason>,
  isArcadeDesignLanguage: boolean,
  isReversedCheckbox: boolean = false
): GetSchemaAndFormDataResult => {
  const showAddressForm = COUNTRIES_WITH_BILLING_ADDRESS_REQUIREMENTS.includes(initialAddressDetails?.countryCode as Country)
  const initialFormData = getInitialFormData(initialCardDetails, initialAddressDetails)
  const { billingCheckboxTicked, displayErrors, showCardFieldHeading, showCreditCardMessage, showCardHolderNameField } = schemaUpdates
  const testNameSpace = 'payment-method'
  const blockedBins = Object.keys(binsToErrorsMap)

  const schema: Schema<NewPaymentMethodFormSchema> = {
    cardField: getCardFieldSchema({
      acceptableCardBrands: enabledCardTypes,
      customizedHeading: null,
      showCardIcon: isArcadeDesignLanguage,
      blockedBins,
      defaultValues: { cardHolderName: initialFormData.cardField.cardHolderName, cardNumber: '', expiryDate: '', cvv: '' },
      errorMessages: {
        cardHolderName: { empty: defaultError(t, 'cardHolderName') },
        cardNumber: {
          invalid: defaultError(t, 'cardNumber'),
          brandNotAccepted: (selectedCardBrand) =>
            selectedCardBrand === CardBrand.AMEX ? t('paymentMethod:errors:amexUnsupported') : t('paymentMethod:errors:cardNotAccepted', { selectedCardBrand }),
          binNotAccepted: (cardNumber) => getBinNotAcceptedErrorMessage(cardNumber, blockedBins, binsToErrorsMap, t),
        },
        cvv: {
          invalid: defaultError(t, 'cardCvv'),
        },
        expiryDate: {
          empty: defaultError(t, 'cardExpiry'),
          past: t('paymentMethod:errors.expiryPast'),
          expireSoon: t('paymentMethod:errors:cardExpiresSoon'),
          expireInLimitMonths: t(`paymentMethod:errors:cardExpiryLimitInMonths`, { count: PCL_CARD_EXPIRY_LIMIT_IN_MONTHS }),
        },
        textSize: 'M',
      },
      fieldTitle: getCardTypeTitle(feature, t),
      fieldTitleSize: 'M',
      labels: {
        cardNumber: t('paymentMethod:cardNumber'),
        cvv: t('paymentMethod:cardCvv'),
        expiryDate: t('paymentMethod:cardExpiry'),
        cardHolderName: t('paymentMethod:cardHolderName'),
      },
      lastPaymentDate,
      showCardHolderNameField,
      showHeading: showCardFieldHeading,
      testNameSpace,
      feature,
    }),
    billingAndProfileAddressEqual: getCheckboxFieldSchema({
      value: true,
      show: showAddressForm,
      otherProps: {
        id: 'new-payment-method-billing-and-profile-address-equal-checkbox',
        label: t('paymentMethod:billingAddressCheckboxLabel'),
        size: 'Medium',
        bold: isArcadeDesignLanguage,
        reversed: isReversedCheckbox,
        'data-dd-action-name': 'New Payment Method Billing and Profile Address Equal Checkbox',
      },
    }),
    address: getAddressFieldSchema({
      value: initialFormData.address,
      disabled: false,
      show: !billingCheckboxTicked,
      otherProps: {
        label: t('addNewAddress:addressInputLabel'),
        manualAddressLabel: t('addNewAddress:findAddressLabel'),
        address1Label: t('addNewAddress:address1Label'),
        address2Label: t('addNewAddress:address2Label'),
        postcodeLabel: t('addNewAddress:postcodeLabel'),
        componentRestrictions: { country: accountCountry },
        suburbLabel: t('addNewAddress:suburbLabel'),
        stateLabel: t('addNewAddress:stateLabel'),
        optionalManualAddressFields: getAddressOptionalFields(accountCountry),
        manualAddressLabelCaption: t('addNewAddress:findAddressLabelCaption'),
        icon: 'Search',
        iconPosition: 'Left',
        showManualAddressLabelInMenu: true,
        showManualAddressFieldsOnSelect: true,
        defaultExpanded: true,
        'data-dd-action-name': 'New Payment Method Address Input',
      },
      options: {
        validate: {
          ...requiredAddress({ name: 'address', country: accountCountry, t }),
        },
      },
      testNameSpace,
    }),
    creditCardMessage: {
      show: showCreditCardMessage,
      type: FieldType.Component,
      Component: components.creditCardMessage,
    },
    addressPreview: {
      type: FieldType.Component,
      show: billingCheckboxTicked && showAddressForm,
      Component: components.addressPreview,
    },
    errors: {
      show: displayErrors,
      type: FieldType.Component,
      Component: components.errors,
    },
    submitButton: {
      type: FieldType.Component,
      Component: components.submitButton,
    },
  }
  return { schema, initialFormData }
}
